import {
    Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, InputAdornment, TextField, Tooltip, Typography
} from "@mui/material"
import {
    AddOutlined, CloseOutlined, DeleteOutlineOutlined, EditOutlined, EmailOutlined,
    FileDownloadOutlined, HomeWorkOutlined, InfoOutlined, PhoneOutlined, Refresh,
    SearchOutlined, UploadFileOutlined
} from "@mui/icons-material"
import { FC, useEffect, useRef, useState } from "react"
import * as XLSX from "xlsx"
import PageTitle from "../../Common/PageTitle"
import NoData from "../../Common/NoData"
import MenuListPop from "../../Common/menu"
import { Apis, normalizePhoneNumber, numberWithCommas } from "../../../helpers"
import { EventsEmit } from "../../../configs/events"
import { excelOnboardFile } from "../../../configs"
import { PriorTenantProps } from "../../../types/tenants"
import { PropertyProps } from "../../../types/properties"
import { Connect, mapStateToProps } from "../../../Redux"
import { theme } from "../../../theme"
import RecordDialog from "./recordDialog"

const PRIMARY = theme.lightTheme.palette.primary.main
const AVATAR_COLORS = ['#E57373', '#F06292', '#BA68C8', '#64B5F6', '#4DB6AC', '#81C784', '#FFB74D', '#A1887F']

interface Props {
    selectedProperty?: PropertyProps
}

interface StateProps {
    data: PriorTenantProps[]
    loading: boolean
    refreshing: boolean
    uploading: boolean
    saving: boolean
    searchVal: string
    templateColumns: number | null
    showInstructions: boolean
    instructions: string[]
    dialog: { record: PriorTenantProps | null, standalone: boolean, index: number | null } | null
    confirmDeleteAll: boolean
}

const financialFields = (item: PriorTenantProps) => ([
    { label: 'Deposit', value: item.deposit },
    { label: 'Paid', value: item.paidDeposit },
    { label: 'Rent/mo', value: item.pricePerMonth },
    { label: 'Arrears', value: item.arrears },
    { label: 'Balance', value: item.balance },
])

const UploadData: FC<Props> = ({ selectedProperty }) => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [state, setState] = useState<StateProps>({
        data: [],
        loading: true,
        refreshing: false,
        uploading: false,
        saving: false,
        searchVal: '',
        templateColumns: null,
        showInstructions: false,
        instructions: [],
        dialog: null,
        confirmDeleteAll: false
    })
    const { data, loading, refreshing, uploading, saving, searchVal, showInstructions, instructions, dialog, confirmDeleteAll } = state

    const Data = data
        .filter((item, index, self) => index === self.findIndex(t => t.unitLabel === item.unitLabel && t.unit === item.unit))
        .filter(item => !searchVal ||
            (item.name || '').toLowerCase().includes(searchVal.toLowerCase()) ||
            (item.email || '').toLowerCase().includes(searchVal.toLowerCase()) ||
            (item.unit || '').toLowerCase().includes(searchVal.toLowerCase()) ||
            (item.unitLabel || '').toLowerCase().includes(searchVal.toLowerCase())
        )
    const newTenants = data.filter(q => !q._id)

    const fetchPriorTenants = () => {
        Apis({ module: 'Tenants', action: 'getPriorTenant', propertyId: selectedProperty?._id })
            .then((resp: any) => setState(s => ({ ...s, data: resp || [], loading: false, refreshing: false, saving: false })))
            .catch(() => setState(s => ({ ...s, loading: false, refreshing: false, saving: false })))
    }

    useEffect(() => {
        fetchPriorTenants()
        Apis({ module: 'Properties', action: 'dataUploadDecription' })
            .then((resp: any) => setState(s => ({ ...s, instructions: resp || [] })))
            .catch(() => { })
        // eslint-disable-next-line
    }, [])

    const getExpectedColumnCount = async (): Promise<number> => {
        if (state.templateColumns) return state.templateColumns
        try {
            const resp = await fetch(excelOnboardFile)
            const buffer = await resp.arrayBuffer()
            const workbook = XLSX.read(buffer, { type: 'array' })
            const worksheet = workbook.Sheets[workbook.SheetNames[0]]
            const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
            const count = rows[0]?.length || 10
            setState(s => ({ ...s, templateColumns: count }))
            return count
        } catch {
            return 10
        }
    }

    const handleFile = async (file: File) => {
        setState(s => ({ ...s, uploading: true }))
        try {
            const buffer = await file.arrayBuffer()
            const workbook = XLSX.read(buffer, { type: 'array' })
            const worksheet = workbook.Sheets[workbook.SheetNames[0]]
            const rows: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
            const expectedColumns = await getExpectedColumnCount()
            const dataRows = rows.slice(1).filter(row => row.length === expectedColumns)
            const parsed: PriorTenantProps[] = dataRows.map(row => ({
                name: row[0],
                email: row[1],
                phone: normalizePhoneNumber(row[2]),
                unit: row[3],
                deposit: Number(row[4]) || 0,
                pricePerMonth: Number(row[5]) || 0,
                unitLabel: row[6],
                paidDeposit: Number(row[7]) || 0,
                arrears: Number(row[8]) || 0,
                balance: Number(row[9]) || 0,
                propertyId: selectedProperty?._id
            }))
            if (!parsed.length) {
                setState(s => ({ ...s, uploading: false }))
                EventsEmit('app', { info: { message: 'The format of your data seems incorrect. Please download our Excel file for reference.', status: 'error' } })
            } else
                setState(s => ({ ...s, data: [...s.data, ...parsed], uploading: false }))
        } catch (err) {
            setState(s => ({ ...s, uploading: false }))
            EventsEmit('app', { info: { message: 'Could not read the file. Please try again.', status: 'error' } })
        }
    }

    const deleteItem = (item: PriorTenantProps, index: number) => {
        const NewData = [...data]
        NewData.splice(index, 1)
        setState(s => ({ ...s, data: NewData }))
        if (item._id)
            Apis({ module: 'Tenants', action: 'deletePriorTenant', itemIds: [item._id] })
                .then(({ message }: any) => EventsEmit('app', { info: { message, status: 'success' } }))
                .catch(() => { })
    }

    const deleteAll = () => {
        const ids = data.filter(q => q._id).map(q => q._id) as string[]
        setState(s => ({ ...s, data: [], confirmDeleteAll: false }))
        if (ids.length)
            Apis({ module: 'Tenants', action: 'deletePriorTenant', itemIds: ids }).catch(() => { })
    }

    const save = () => {
        if (!newTenants.length || !selectedProperty) return
        setState(s => ({ ...s, saving: true }))
        Apis({
            module: 'Tenants',
            action: 'addPriorTenants',
            newTenants,
            property: { name: selectedProperty.name, logo: selectedProperty.logos?.small?.url, isExternalPayment: selectedProperty.isExternalPayment }
        })
            .then(() => {
                EventsEmit('app', { info: { message: 'Tenant data has been saved.', status: 'success' } })
                fetchPriorTenants()
            })
            .catch(() => setState(s => ({ ...s, saving: false })))
    }

    return (
        <Box sx={{ pr: 5, height: '100%', pb: 5 }}>
            <PageTitle title="Upload Tenants Data" backBtn />

            {/* Toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        startIcon={<AddOutlined />}
                        sx={{ textTransform: 'none' }}
                        onClick={() => setState(s => ({ ...s, dialog: { record: null, standalone: true, index: null } }))}
                    >
                        Add Tenant
                    </Button>
                    <Button
                        startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileOutlined />}
                        sx={{ textTransform: 'none' }}
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload File
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        hidden
                        onChange={e => {
                            const file = e.target.files?.[0]
                            if (file) handleFile(file)
                            e.target.value = ''
                        }}
                    />
                    <Button
                        component="a"
                        href={excelOnboardFile}
                        startIcon={<FileDownloadOutlined />}
                        sx={{ textTransform: 'none' }}
                    >
                        Download Template
                    </Button>
                    <Tooltip title="How it works">
                        <IconButton size="small" onClick={() => setState(s => ({ ...s, showInstructions: true }))}>
                            <InfoOutlined sx={{ fontSize: 19, color: 'text.secondary' }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {Boolean(data.length) && (
                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchVal}
                            onChange={e => setState(s => ({ ...s, searchVal: e.target.value }))}
                            sx={{ width: 220, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: 2 } }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment> }}
                        />
                    )}
                    <Tooltip title="Refresh">
                        <IconButton
                            size="small"
                            onClick={() => { setState(s => ({ ...s, refreshing: true })); fetchPriorTenants() }}
                            disabled={refreshing}
                        >
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                    {Boolean(data.length) && (
                        <Button
                            color="error"
                            sx={{ textTransform: 'none' }}
                            onClick={() => setState(s => ({ ...s, confirmDeleteAll: true }))}
                        >
                            Delete All
                        </Button>
                    )}
                    {Boolean(newTenants.length) && (
                        <Button
                            variant="contained"
                            sx={{ textTransform: 'none', borderRadius: 2 }}
                            disabled={saving}
                            onClick={save}
                        >
                            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : `Save (${newTenants.length})`}
                        </Button>
                    )}
                </Box>
            </Box>

            {!Data.length || loading ? (
                <NoData
                    message="No tenant data yet."
                    searchVal={searchVal}
                    loading={loading}
                    createMessage={searchVal ? undefined : 'Upload File'}
                    onCreate={() => fileInputRef.current?.click()}
                />
            ) : (
                <Box>
                    {Data.map((item) => {
                        const index = data.indexOf(item)
                        const isNew = !item._id
                        const initials = (item.name || '?').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
                        const avatarBg = AVATAR_COLORS[(item.name || '?').charCodeAt(0) % AVATAR_COLORS.length]
                        return (
                            <Box
                                key={(item._id || index) + '-' + item.unitLabel}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff', borderRadius: 3, p: 2, mb: 1.5,
                                    boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)', flexWrap: 'wrap'
                                }}
                            >
                                <Avatar sx={{ bgcolor: avatarBg, width: 44, height: 44, fontWeight: 800 }}>{initials}</Avatar>

                                <Box sx={{ flex: '0 0 220px', minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 800 }} noWrap>{item.name}</Typography>
                                        {isNew && (
                                            <Chip label="NEW" size="small" sx={{ height: 18, fontSize: 10, fontWeight: 800, bgcolor: `${PRIMARY}1f`, color: PRIMARY }} />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4 }}>
                                        <PhoneOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
                                        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{item.phone || '—'}</Typography>
                                    </Box>
                                    {item.email && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2 }}>
                                            <EmailOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
                                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{item.email}</Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: `${PRIMARY}14`, color: PRIMARY,
                                    borderRadius: 2, px: 1.2, py: 0.5, flexShrink: 0
                                }}>
                                    <HomeWorkOutlined sx={{ fontSize: 14 }} />
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{item.unitLabel} · {item.unit}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 2.5, flex: 1, flexWrap: 'wrap' }}>
                                    {financialFields(item).map(({ label, value }) => (
                                        <Box key={label} sx={{ minWidth: 68 }}>
                                            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .3 }}>
                                                {label}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12.5, fontWeight: 800 }}>
                                                Ksh {numberWithCommas(value || 0)}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                <MenuListPop
                                    menuList={[
                                        { label: 'Edit', icon: <EditOutlined /> },
                                        { label: 'Delete', icon: <DeleteOutlineOutlined /> }
                                    ]}
                                    onSelect={(option) => {
                                        if (option === 'Edit')
                                            setState(s => ({ ...s, dialog: { record: item, standalone: false, index } }))
                                        else
                                            deleteItem(item, index)
                                    }}
                                />
                            </Box>
                        )
                    })}
                </Box>
            )}

            {dialog && selectedProperty && (
                <RecordDialog
                    record={dialog.record}
                    standalone={dialog.standalone}
                    property={selectedProperty}
                    close={() => setState(s => ({ ...s, dialog: null }))}
                    onSaved={fetchPriorTenants}
                    onLocalUpdate={(obj) => {
                        if (dialog.index == null) return
                        const NewData = [...data]
                        NewData[dialog.index] = { ...NewData[dialog.index], ...obj }
                        setState(s => ({ ...s, data: NewData }))
                    }}
                />
            )}

            <Dialog open={showInstructions} onClose={() => setState(s => ({ ...s, showInstructions: false }))} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    How it works
                    <IconButton size="small" onClick={() => setState(s => ({ ...s, showInstructions: false }))}>
                        <CloseOutlined sx={{ fontSize: 18 }} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {instructions.map((step, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.2, mb: 1.5 }}>
                            <Box sx={{
                                width: 20, height: 20, borderRadius: '50%', bgcolor: `${PRIMARY}1f`, color: PRIMARY,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0
                            }}>
                                {i + 1}
                            </Box>
                            <Typography sx={{ fontSize: 13.5 }}>{step}</Typography>
                        </Box>
                    ))}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        component="a"
                        href={excelOnboardFile}
                        variant="contained"
                        startIcon={<FileDownloadOutlined />}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Download Template
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteAll} onClose={() => setState(s => ({ ...s, confirmDeleteAll: false }))} maxWidth="xs" fullWidth>
                <DialogTitle>Delete All Data</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 14 }}>
                        Are you sure you want to delete all tenant data for this property?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button sx={{ textTransform: 'none' }} onClick={() => setState(s => ({ ...s, confirmDeleteAll: false }))}>Cancel</Button>
                    <Button color="error" variant="contained" sx={{ textTransform: 'none' }} onClick={deleteAll}>Delete All</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default Connect(mapStateToProps)(UploadData)
